// Staff home (qms / supervisor / admin) — the mockup's "Genel Bakış".
// A supervisor's request is brand-scoped by the API, so this same screen shows the whole
// fleet to QMS and only their own team to a supervisor, with no branching here.

import * as api from '../api';
import type { Meta, PanelUser } from '../types';
import { useAsync, usePeriodPicker } from '../hooks';
import { Avatar, Callout, CriterionBar, Empty, Icon, Kpi, Loading, Note, PageHead, Panel, Pill, ScorePill } from '../ui';
import { delta10, score10 } from '../format';
import { navigate } from '../App';
import PeriodBar from './PeriodBar';
import AgentOfWeek from './AgentOfWeek';

export default function QmsDashboard({ user, meta, brand }: {
  user: PanelUser; meta: Meta | null;
  /** The topbar's global brand filter; '' = every brand this user may see. */
  brand: string;
}) {
  const picker = usePeriodPicker(meta);
  const state = useAsync(
    () => api.getOverview(picker.id, brand || undefined),
    [picker.id, brand],
  );
  const brandName = meta?.brands.find((b) => b.key === brand)?.name ?? null;

  // Every non-agent may create now (supervisors too, owner 2026-09). This screen only renders
  // for non-agents, so the button is effectively always shown — kept explicit for clarity.
  const canCreate = user.role !== 'agent';
  const head = (
    <PageHead
      icon="dashboard"
      title="Genel Bakış"
      subtitle={state.data
        ? `${state.data.period.label}${brandName ? ` · ${brandName}` : ''} · kalite ekibi performansı`
        : ' '}
      right={(
        <PeriodBar
          picker={picker}
          right={canCreate ? (
            <button className="btn pri" type="button" onClick={() => navigate('new')}>
              <Icon name="add" /><span>Yeni Değerlendirme</span>
            </button>
          ) : undefined}
        />
      )}
    />
  );

  if (state.error) return <>{head}<Note error>{state.error}</Note></>;
  if (!state.data) return <>{head}<Loading /></>;

  const r = state.data;
  const deltaText = delta10(r.summary.avgScore, r.previous.avgScore);
  const deltaKind = r.summary.avgScore !== null && r.previous.avgScore !== null
    ? (r.summary.avgScore >= r.previous.avgScore ? 'up' : 'down') : 'flat';
  const evalDelta = r.summary.evalCount - r.previous.evalCount;
  const weakest = [...r.criteria].filter((c) => c.avg !== null)
    .sort((a, b) => (a.avg as number) - (b.avg as number))[0];
  const critTotal = r.criticals.reduce((n, c) => n + c.count, 0);

  return (
    <>
      {head}

      <div className="kpis">
        <Kpi
          label="Bu dönem değerlendirilen" value={r.summary.evalCount}
          icon="fact_check" tone="accent"
          delta={`${evalDelta >= 0 ? '▲' : '▼'} ${Math.abs(evalDelta)} · önceki döneme göre`}
          deltaKind={evalDelta >= 0 ? 'up' : 'down'}
        />
        <Kpi
          label="Ortalama kalite skoru" value={score10(r.summary.avgScore)} unit="/10"
          icon="trending_up" tone="good"
          delta={deltaText ? `${deltaText} · önceki döneme göre` : 'karşılaştırılacak dönem yok'}
          deltaKind={deltaText ? deltaKind : 'flat'}
        />
        <Kpi
          label="Kritik hatalar" value={r.summary.criticalCount}
          icon="warning" tone={r.summary.criticalCount ? 'bad' : 'good'}
          delta={r.summary.criticalCount ? 'takip gerekiyor' : 'temiz dönem'}
          deltaKind={r.summary.criticalCount ? 'down' : 'up'}
        />
        <Kpi
          label="Değerlendirilen agent" value={r.summary.agentCount}
          icon="groups" tone="warn"
          delta="bu dönem en az bir çağrısı puanlanan" deltaKind="flat"
        />
        <Kpi
          label="Açık itiraz" value={r.openObjections}
          icon="gavel" tone="neu"
          delta={r.openObjections ? 'yanıt bekliyor' : 'kuyruk boş'} deltaKind="flat"
        />
      </div>

      {/* Haftanın Temsilcisi · 24 saat (#292/#293) — live, its own request so a slow call-agent-admin never holds the page. */}
      <AgentOfWeek period={picker.id} brand={brand} />

      <div className="grid-2">
        <Panel
          icon="leaderboard"
          title="Agent sıralaması"
          hint={r.period.label}
          right={<button className="link" type="button" onClick={() => navigate('evaluations')}>Tüm değerlendirmeler →</button>}
        >
          {r.agents.length === 0 ? (
            <Empty>Bu dönemde değerlendirme yok.</Empty>
          ) : (
            <div className="tbl-wrap">
              <table>
                <thead>
                  <tr><th>#</th><th>Agent</th><th>Marka</th><th>Değ.</th><th>Ortalama</th><th>Kritik</th><th /></tr>
                </thead>
                <tbody>
                  {r.agents.map((a) => (
                    <tr key={a.agentId}>
                      <td><span className={`rank${a.rank <= 3 ? ' top' : ''}`}>{a.rank}</span></td>
                      <td>
                        <div className="who">
                          <Avatar name={a.agentName} kind={a.criticalCount ? 'bad' : a.rank <= 3 ? 'good' : 'neutral'} />
                          <span className="nm">{a.agentName}</span>
                        </div>
                      </td>
                      <td><Pill tone="neu">{a.brandKey}</Pill></td>
                      <td className="tnum">{a.evalCount}</td>
                      <td><ScorePill score={a.avgScore} /></td>
                      <td className="tnum" style={{ color: a.criticalCount ? 'var(--bad)' : 'var(--faint)', fontWeight: 700 }}>
                        {a.criticalCount}
                      </td>
                      <td className="r">
                        <button className="link" type="button" onClick={() => navigate(`agent/${a.agentId}`)}>Panosu</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <div className="stack">
          <Panel icon="bar_chart" title="Kritere göre ortalama" hint={r.period.label}>
            <div className="panel-body tight">
              {r.criteria.map((c) => (
                <CriterionBar key={c.key} label={c.label} value={c.avg} weight={c.weight} />
              ))}
              <div style={{ marginTop: 12 }}>
                {weakest ? (
                  <Callout tone="bad" icon="priority_high">
                    <b>{weakest.label}</b>
                    <br />
                    {`${score10(weakest.avg)}/10 ile en düşük kriter · ağırlığı %${weakest.weight}`}
                  </Callout>
                ) : (
                  <Callout>Bu dönemde puanlanmış kriter yok.</Callout>
                )}
              </div>
            </div>
          </Panel>

          <Panel icon="error" tone="bad" title="Kritik hata dağılımı" hint={critTotal ? `${critTotal} işaret · her biri çağrıyı 0 yaptı` : 'bu dönemde kritik hata yok'}>
            {critTotal ? (
              <div className="critlist">
                {r.criticals.filter((c) => c.count > 0).map((c) => (
                  <div key={c.key} className="critline">
                    <span className="tag bad">×{c.count}</span>
                    <span className="grow">{c.label}</span>
                  </div>
                ))}
              </div>
            ) : (
              <Empty>Temiz dönem.</Empty>
            )}
          </Panel>
        </div>
      </div>
    </>
  );
}
